import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    TextField,
    Button,
    Typography,
    Alert,
    Container,
    LinearProgress,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

export default function ChangePasswordPage() {
    const { user, token, logout } = useAuth();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    // Password strength indicator
    const [passwordStrength, setPasswordStrength] = useState(0);

    const calculatePasswordStrength = (password: string): number => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/\d/.test(password)) strength += 12.5;
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength += 12.5;
        return Math.min(strength, 100);
    };

    const handlePasswordChange = (password: string) => {
        setNewPassword(password);
        setPasswordStrength(calculatePasswordStrength(password));
    };

    const getPasswordStrengthColor = () => {
        if (passwordStrength < 40) return 'error';
        if (passwordStrength < 70) return 'warning';
        return 'success';
    };

    const getPasswordStrengthLabel = () => {
        if (passwordStrength < 40) return 'Weak';
        if (passwordStrength < 70) return 'Medium';
        return 'Strong';
    };

    const validatePassword = (): boolean => {
        if (!currentPassword) {
            setError('Please enter your current password');
            return false;
        }

        if (!newPassword || !confirmPassword) {
            setError('Please enter both new password fields');
            return false;
        }

        if (newPassword.length < 8) {
            setError('New password must be at least 8 characters long');
            return false;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return false;
        }

        if (currentPassword === newPassword) {
            setError('New password must be different from current password');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!validatePassword()) {
            return;
        }

        setIsLoading(true);

        try {
            const API_URL = process.env.REACT_APP_API_URL;
            const response = await fetch(`${API_URL}/auth/change-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword,
                }),
            });

            if (response.ok) {
                setSuccess('Password changed successfully! You will be logged out in 3 seconds...');
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');

                // Logout and redirect to login after 3 seconds
                setTimeout(() => {
                    logout();
                    navigate('/login');
                }, 3000);
            } else {
                const errorData = await response.json();
                setError(errorData.detail || 'Failed to change password. Please check your current password.');
            }
        } catch (err) {
            console.error('Change password error:', err);
            setError('An error occurred. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => {
        navigate('/profile');
    };

    return (
        <Container component="main" maxWidth="sm">
            <Box sx={{ mt: 4, mb: 4 }}>
                <Card>
                    <CardContent sx={{ p: 4 }}>
                        <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                            Change Password
                        </Typography>

                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Update your password to keep your account secure
                        </Typography>

                        <Box component="form" onSubmit={handleSubmit}>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            {success && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    {success}
                                </Alert>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="currentPassword"
                                label="Current Password"
                                type="password"
                                id="currentPassword"
                                autoComplete="current-password"
                                autoFocus
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                disabled={isLoading}
                            />

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="newPassword"
                                label="New Password"
                                type="password"
                                id="newPassword"
                                autoComplete="new-password"
                                value={newPassword}
                                onChange={(e) => handlePasswordChange(e.target.value)}
                                disabled={isLoading}
                                sx={{ mt: 3 }}
                            />

                            {newPassword && (
                                <Box sx={{ mt: 1, mb: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" color="textSecondary">
                                            Password Strength:
                                        </Typography>
                                        <Typography variant="caption" color={`${getPasswordStrengthColor()}.main`}>
                                            {getPasswordStrengthLabel()}
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant="determinate"
                                        value={passwordStrength}
                                        color={getPasswordStrengthColor()}
                                        sx={{ height: 6, borderRadius: 3 }}
                                    />
                                </Box>
                            )}

                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                name="confirmPassword"
                                label="Confirm New Password"
                                type="password"
                                id="confirmPassword"
                                autoComplete="new-password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                disabled={isLoading}
                            />

                            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    disabled={isLoading}
                                    sx={{ py: 1.5 }}
                                >
                                    {isLoading ? 'Changing Password...' : 'Change Password'}
                                </Button>

                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleCancel}
                                    disabled={isLoading}
                                    sx={{ py: 1.5 }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Container>
    );
}
